import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Send, CheckCircle2, Clock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SIZES = ["10ml", "50ml", "100ml", "150ml", "250ml"];

const WhatsAppIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

export const PreOrderForm = ({ product, waNumber }) => {
  const [size, setSize] = useState("100ml");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (name.trim().length < 2) return toast.error("Nama wajib diisi");
    if (phone.replace(/\D/g, "").length < 9) return toast.error("Nomor WhatsApp tidak valid");
    setSending(true);
    try {
      await axios.post(`${API}/preorders`, { product_id: product.product_id, name, phone, size, note });
      setDone(true);
      toast.success("Pesanan PO diterima! Kami akan menghubungi Anda");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal mengirim pesanan");
    } finally {
      setSending(false);
    }
  };

  const waLink = waNumber
    ? `https://wa.me/${waNumber}?text=${encodeURIComponent(
        `Halo NALURA! Saya ingin pre-order ${product.name} ukuran ${size}.${name.trim() ? ` Nama saya ${name.trim()}.` : ""}`
      )}`
    : null;

  if (done) {
    return (
      <div data-testid="preorder-success" className="border border-[#1B4332]/30 bg-[#1B4332]/5 p-8 mb-10 text-center">
        <CheckCircle2 className="w-10 h-10 text-[#1B4332] mx-auto mb-4" />
        <p className="font-display text-2xl tracking-tight mb-2">Pesanan PO Anda Diterima!</p>
        <p className="text-sm text-[#211B10]/70 mb-6">
          Kami akan menghubungi Anda via WhatsApp untuk konfirmasi. Estimasi parfum siap dalam 2–4 minggu.
        </p>
        {waLink && (
          <a data-testid="preorder-wa-after" href={waLink} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-[#25D366] text-white text-xs font-medium uppercase tracking-[0.12em] px-6 py-3 hover:opacity-90 transition-opacity">
            <WhatsAppIcon className="w-4 h-4" /> Chat Kami Sekarang
          </a>
        )}
      </div>
    );
  }

  return (
    <div data-testid="preorder-form" className="border border-[#A67C1E]/40 bg-[#A67C1E]/5 p-8 mb-10">
      <div className="flex items-center justify-between gap-4 flex-wrap mb-2">
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#A67C1E]">Open Pre-Order</p>
        <span className="inline-flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-[0.18em] text-[#211B10]/60">
          <Clock className="w-3.5 h-3.5 text-[#A67C1E]" /> Siap dalam 2–4 minggu
        </span>
      </div>
      <p className="text-sm text-[#211B10]/70 leading-relaxed mb-6">
        Pesan sekarang, parfum Anda kami siapkan. Harga akan diumumkan menyusul — pemesan PO mendapat prioritas & penawaran terbaik.
      </p>

      <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#211B10]/60 mb-3">Pilih Ukuran</p>
      <div className="flex flex-wrap gap-2 mb-2">
        {SIZES.map((s) => (
          <button
            key={s}
            type="button"
            data-testid={`po-size-${s}`}
            onClick={() => setSize(s)}
            className={`px-4 py-2.5 font-mono text-xs uppercase tracking-[0.12em] border transition-colors duration-200 ${
              size === s ? "bg-[#A67C1E] text-[#FBF7EE] border-[#A67C1E]" : "bg-transparent text-[#211B10]/70 border-[#211B10]/20 hover:border-[#A67C1E]/60 hover:text-[#A67C1E]"
            }`}
          >
            {s}
          </button>
        ))}
      </div>
      <p className="font-mono text-[9px] uppercase tracking-[0.15em] text-[#211B10]/55 mb-6">Tip: pilih 250ml untuk ruangan luas</p>

      <form onSubmit={submit} className="space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <input
            data-testid="po-name-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Nama Anda"
            className="w-full bg-white border border-[#211B10]/15 px-4 py-3 text-sm focus:outline-none focus:border-[#A67C1E] placeholder:text-[#211B10]/35"
          />
          <input
            data-testid="po-phone-input"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="No. WhatsApp (08xx / 62xx)"
            inputMode="tel"
            className="w-full bg-white border border-[#211B10]/15 px-4 py-3 text-sm focus:outline-none focus:border-[#A67C1E] placeholder:text-[#211B10]/35"
          />
        </div>
        <input
          data-testid="po-note-input"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Catatan (opsional)"
          className="w-full bg-white border border-[#211B10]/15 px-4 py-3 text-sm focus:outline-none focus:border-[#A67C1E] placeholder:text-[#211B10]/35"
        />
        <div className="flex flex-wrap gap-3 pt-2">
          <button
            type="submit"
            data-testid="po-submit-button"
            disabled={sending}
            className="inline-flex items-center gap-2 bg-[#A67C1E] text-[#FBF7EE] font-medium text-sm uppercase tracking-[0.12em] px-7 py-3.5 hover:bg-[#211B10] transition-colors duration-300 disabled:opacity-60"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Kirim Pesanan PO
          </button>
          {waLink && (
            <a
              data-testid="po-whatsapp-button"
              href={waLink}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#25D366] text-white font-medium text-sm uppercase tracking-[0.12em] px-7 py-3.5 hover:opacity-90 transition-opacity"
            >
              <WhatsAppIcon className="w-4 h-4" /> PO via WhatsApp
            </a>
          )}
        </div>
      </form>
    </div>
  );
};
